import React from "react";
import { useTranslation } from "react-i18next";
import {
    Filter,
    Grid,
    useBaseAppContext,
    useResourceApi,
    useFormContext,
    numberFormat,
    numberFormatCurrency,
} from "@programari-limit/base-react";
import MuiGrid from "@mui/material/Grid";
import GridFormField from "components/GridFormField";
import {
    columnesArticle,
    columnesOperacions,
} from "sharedAdvancedSearch/advancedSearchColumns";
import { suffixValueFormatter } from "utils/muiUtils";
import {
    Box,
    Divider,
    darken,
    Icon,
    IconButton,
    Tooltip,
    Typography,
} from "@mui/material";
import GridFilterField from "components/GridFilterField";
import { SpringFilterQueryBuilder } from "utils/spring-filter-query-builder";
import { getFielYellow } from "pages/reserves/Reserves";

const FilterContent = (props) => {
    const { filterApiRef } = props;
    const { t } = useTranslation();

    return (
        <MuiGrid container spacing={2}>
            <GridFilterField name="tipusOperacio" md={6} lg={2} />
            <GridFilterField name="dataInici" xs={6} md={6} lg={1.75} />
            <GridFilterField name="dataFi" xs={6} md={6} lg={1.75} />
            <GridFilterField
                name="article"
                md={6}
                lg={2}
                advancedSearchColumns={columnesArticle}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFilterField name="recurs" md={6} lg={2} />
            <GridFilterField name="embarcacio" md={6} lg={2} />

            <MuiGrid item lg={0.5}>
                <Tooltip title={t("comu.resetFiltre")}>
                    <IconButton
                        onClick={() => filterApiRef?.current?.doReset()}
                    >
                        <Icon>filter_alt_off</Icon>
                    </IconButton>
                </Tooltip>
            </MuiGrid>
        </MuiGrid>
    );
};

const TotalsContent = (props) => {
    const { totals } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useBaseAppContext();

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                flexWrap: "wrap",
                gap: 5,
                mt: 1,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.operacions")}
                </Typography>
                <Typography>
                    {numberFormat(
                        totals.totalOperacions,
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.totals.total")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.importTotal,
                        "eur",
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.linies.totals.preuMig")}
                </Typography>
                <Typography>
                    {numberFormatCurrency(
                        totals.preuMig,
                        "eur",
                        null,
                        currentLanguage
                    )}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <Typography variant="h6">
                    {t("operacio.linies.totals.totalQuantitat")}
                </Typography>
                <Typography>
                    {numberFormat(totals.totalQuantitat, null, currentLanguage)}
                </Typography>
            </Box>
        </Box>
    );
};

const springFilterBuilder = (data) => {
    const { and, equal, ge, le } = SpringFilterQueryBuilder;

    const filter = and(
        equal("operacio.tipusOperacio", data?.tipusOperacio),
        ge(
            "operacio.data",
            data?.dataInici ? data?.dataInici + "T00:00:00" : undefined
        ),
        le(
            "operacio.data",
            data?.dataFi ? data?.dataFi + "T23:59:59" : undefined
        ),
        equal("operacio.embarcacio.id", data?.embarcacio?.id),
        equal("operacio.ruta.recurs.id", data?.recurs?.id),
        equal("article.id", data?.article?.id)
    ).toString();

    return filter;
};

const OperacioLiniaFilter = (props) => {
    const { onSpringFilterChange, onFilterInitialized } = props;
    const filterApiRef = React.useRef();

    return (
        <Box sx={{ mb: 2 }}>
            <Filter
                name={"OL_FILTER"}
                resourceName={"operacioLinia"}
                persistentState
                springFilterBuilder={springFilterBuilder}
                onSpringFilterChange={onSpringFilterChange}
                onFilterInitialized={onFilterInitialized}
                size="small"
                apiRef={filterApiRef}
                initialOnChangeRequest
            >
                <FilterContent filterApiRef={filterApiRef} />
            </Filter>
        </Box>
    );
};

export const OperacioLiniaForm = ({ noOperacio }) => {
    const { data } = useFormContext();

    return (
        <MuiGrid container spacing={2} sx={{ mt: 1 }}>
            {noOperacio && (
                <GridFormField
                    name="operacio"
                    lg={4}
                    advancedSearchColumns={columnesOperacions}
                    advancedSearchMaxWidth="xl"
                    disabled={!!data?.operacio?.id}
                />
            )}
            <GridFormField
                name="article"
                lg={5}
                advancedSearchColumns={columnesArticle}
                advancedSearchMaxWidth={"lg"}
            />
            <GridFormField name="quantitat" lg={3} />
            <GridFormField name="importLinia" lg={4} suffix={" €"} />
            <GridFormField name="preu" lg={4} suffix={" €"} />
        </MuiGrid>
    );
};

const OperacioLinia = ({ noOperacio, operacio }) => {
    const { t } = useTranslation();
    const [fixedFilter, setFixedFilter] = React.useState();
    const [filterInitialized, setFilterInitialized] = React.useState(false);
    const gridApiRef = React.useRef();
    const { currentLanguage } = useBaseAppContext();
    const { apiInitialized, exec: apiExec } = useResourceApi("operacioLinia");
    const [totals, setTotals] = React.useState();

    const columns = [
        {
            name: "articleNom",
            flex: 1,
        },      
        {
            name: "quantitat",
            flex: 1,
        },
        {
            name: "preu",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
         {
            name: "iva",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " %",
            }
        },
        {
            name: "importLinia",
            flex: 1,
            type: "currency",
            currencyDecimalPlaces: 2,
            formFieldProps: {
                suffix: " €",
            },
            valueFormatter: (params) => suffixValueFormatter(params),
        },
       
    ];

    if (noOperacio) {
        columns.unshift(
            {
                name: "operacio",
                flex: 0.75,
            },
            { name: "dataOperacio", flex: 0.75 }
        );
    }

    // Todo: S'ha posat aixo pq la llibreria no refresca la traduccio de l'accio sense refrescar el grid
    React.useEffect(() => {
        gridApiRef?.current?.doRefresh();
    }, [currentLanguage]);

    const obtenirTotals = () => {
        apiExec("exec_OL_OBTENIR_TOTALS", {
            data: { springFilter: fixedFilter ?? undefined }, // TODO: Ha de poder rebre undefined com a filtre o text buid
            callbacks: {
                setResponse: (response) => {
                    const resource = {
                        ...response.data,
                        _resource: response,
                        _links: response.links,
                    };
                    setTotals(resource);
                },
                setError: (error) => {},
            },
        });
    };

    React.useEffect(() => {
        if (apiInitialized && filterInitialized) {
            obtenirTotals();
        }
    }, [apiInitialized, filterInitialized, fixedFilter]);

    return (
        <Box
            sx={{
                height: "100%",
                "& .cellWarning": {
                    bgcolor: (theme) => getFielYellow(theme.palette.mode),
                },
                "& .MuiDataGrid-row:hover .cellWarning": {
                    bgcolor: (theme) =>
                        `${darken(
                            getFielYellow(theme.palette.mode),
                            0.1
                        )}!important`,
                },
            }}
        >
            <Grid
                title={t("operacio.linies.title")}
                resourceName="operacioLinia"
                columns={columns}
                findDisabled={noOperacio && !filterInitialized}
                actionExecEnabled
                onRefresh={() => obtenirTotals()}
                onDelete={() => obtenirTotals()}
                readOnly
                fixedFilter={
                    noOperacio ? fixedFilter : `operacio.id : '${operacio?.id}'`
                }
                formPopupEditable
                formPopupComponent={<OperacioLiniaForm noOperacio />}
                formPopupTitleCreate={t("operacio.linies.create")}
                formPopupTitleUpdate={t("operacio.linies.update")}
                formPopupTitleView={t("operacio.linies.view")}
                formPopupStaticData={
                    noOperacio
                        ? {
                              entradaManual: true,
                          }
                        : {
                              operacio: { id: operacio?.id },
                              entradaManual: true,
                          }
                }
                toolbarAdditionalRow={
                    noOperacio ? (
                        <>
                            <OperacioLiniaFilter
                                onSpringFilterChange={setFixedFilter}
                                onFilterInitialized={() =>
                                    setFilterInitialized(true)
                                }
                            />
                            {totals && (
                                <Box sx={{ mb: 2 }}>
                                    <TotalsContent totals={totals} />
                                </Box>
                            )}
                        </>
                    ) : undefined
                }
                density="compact"
                getCellClassName={({ row, field }) => {
                    return row?.entradaManual ? "cellWarning" : "";
                }}
            />
        </Box>
    );
};

export default OperacioLinia;
